import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  Users,
} from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { ApiError, apiFetch } from '@/lib/api';

interface Partner {
  id: string;
  fullName: string;
  cpf: string | null;
  email: string | null;
  phone: string | null;
  percentage: string | number;
  joinedAt: string | null;
  exitedAt: string | null;
  isActive: boolean;
  observacoes: string | null;
}

export function PartnersPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Partner | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['partners'],
    queryFn: () => apiFetch<Partner[]>('/partners'),
  });

  const sumQuery = useQuery({
    queryKey: ['partners', 'sum'],
    queryFn: () => apiFetch<{ sum: number; isValid: boolean }>('/partners/percentage-sum'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/partners/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['partners'] });
    },
  });

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Sócios</h1>
          <p className="text-sm text-muted-foreground">
            Cadastro dos sócios com % do contrato social. A soma dos ativos deve
            ser 100 para distribuição de lucros funcionar.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-1 rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-accent disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" /> Novo sócio
          </button>
        </div>
      </header>

      {sumQuery.data && (
        <div
          className={`flex items-center gap-3 rounded-md border p-3 text-sm ${
            sumQuery.data.isValid
              ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
              : 'border-amber-300 bg-amber-50 text-amber-800'
          }`}
        >
          {sumQuery.data.isValid ? (
            <CheckCircle2 className="h-5 w-5" />
          ) : (
            <AlertTriangle className="h-5 w-5" />
          )}
          <div>
            Soma dos % dos sócios ativos:{' '}
            <strong>{Number(sumQuery.data.sum).toFixed(2)}%</strong>
            {!sumQuery.data.isValid && ' — ajuste para 100% antes de distribuir lucros'}
          </div>
        </div>
      )}

      <section className="rounded-lg border border-border bg-card">
        {isLoading && <p className="p-4 text-sm text-muted-foreground">Carregando…</p>}
        {data && data.length === 0 && (
          <div className="flex flex-col items-center gap-3 p-12 text-center">
            <Users className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">Nenhum sócio cadastrado.</p>
          </div>
        )}
        {data && data.length > 0 && (
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/30 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">Nome</th>
                <th className="px-4 py-2 font-medium">CPF</th>
                <th className="px-4 py-2 font-medium">Email</th>
                <th className="px-4 py-2 font-medium text-right">% do contrato</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.map((p) => (
                <tr key={p.id} className="border-b border-border last:border-0 hover:bg-accent/30">
                  <td className="px-4 py-2 font-medium">{p.fullName}</td>
                  <td className="px-4 py-2 font-mono text-xs">{p.cpf ?? '—'}</td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{p.email ?? '—'}</td>
                  <td className="px-4 py-2 text-right font-medium">
                    {Number(p.percentage).toFixed(2)}%
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${
                        p.isActive
                          ? 'bg-emerald-500/10 text-emerald-600'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {p.isActive ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-2 py-2 flex justify-end gap-1">
                    <button
                      type="button"
                      data-dica="Editar"
                      onClick={() => setEditing(p)}
                      className="rounded-md p-2 text-muted-foreground hover:bg-accent"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      data-dica="Excluir"
                      onClick={() => {
                        if (confirm(`Excluir sócio ${p.fullName}?`)) remove.mutate(p.id);
                      }}
                      className="rounded-md p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {showCreate && (
        <PartnerFormDialog
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false);
            qc.invalidateQueries({ queryKey: ['partners'] });
          }}
        />
      )}
      {editing && (
        <PartnerFormDialog
          partner={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ['partners'] });
          }}
        />
      )}
    </div>
  );
}

function PartnerFormDialog({
  partner,
  onClose,
  onSaved,
}: {
  partner?: Partner;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!partner;
  const [form, setForm] = useState({
    fullName: partner?.fullName ?? '',
    cpf: partner?.cpf ?? '',
    email: partner?.email ?? '',
    phone: partner?.phone ?? '',
    percentage: partner ? Number(partner.percentage) : 0,
    joinedAt: partner?.joinedAt ? partner.joinedAt.slice(0, 10) : '',
    exitedAt: partner?.exitedAt ? partner.exitedAt.slice(0, 10) : '',
    isActive: partner?.isActive ?? true,
    observacoes: partner?.observacoes ?? '',
  });
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body = JSON.stringify({
        ...form,
        cpf: form.cpf || undefined,
        email: form.email || undefined,
        phone: form.phone || undefined,
        joinedAt: form.joinedAt || undefined,
        exitedAt: form.exitedAt || undefined,
        observacoes: form.observacoes || undefined,
      });
      return isEdit
        ? apiFetch(`/partners/${partner!.id}`, { method: 'PATCH', body })
        : apiFetch('/partners', { method: 'POST', body });
    },
    onSuccess: onSaved,
    onError: (err) =>
      setError(err instanceof ApiError ? err.body : err instanceof Error ? err.message : 'erro'),
  });

  function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    save.mutate();
  }

  return (
    <Dialog title={isEdit ? `Editar — ${partner!.fullName}` : 'Novo sócio'} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label="Nome completo *">
          <input
            required
            className="input"
            value={form.fullName}
            onChange={(e) => setForm((s) => ({ ...s, fullName: e.target.value }))}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="CPF">
            <input
              className="input"
              value={form.cpf}
              onChange={(e) => setForm((s) => ({ ...s, cpf: e.target.value }))}
            />
          </Field>
          <Field label="% do contrato social *">
            <input
              required
              type="number"
              step="0.01"
              min={0}
              max={100}
              className="input"
              value={form.percentage || ''}
              onChange={(e) =>
                setForm((s) => ({ ...s, percentage: Number(e.target.value) || 0 }))
              }
            />
          </Field>
          <Field label="Email">
            <input
              type="email"
              className="input"
              value={form.email}
              onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
            />
          </Field>
          <Field label="Telefone">
            <input
              className="input"
              value={form.phone}
              onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))}
            />
          </Field>
          <Field label="Entrada na sociedade">
            <input
              type="date"
              className="input"
              value={form.joinedAt}
              onChange={(e) => setForm((s) => ({ ...s, joinedAt: e.target.value }))}
            />
          </Field>
          <Field label="Saída (se aplicável)">
            <input
              type="date"
              className="input"
              value={form.exitedAt}
              onChange={(e) => setForm((s) => ({ ...s, exitedAt: e.target.value }))}
            />
          </Field>
        </div>
        <Field label="Observações">
          <textarea
            rows={2}
            className="input"
            value={form.observacoes}
            onChange={(e) => setForm((s) => ({ ...s, observacoes: e.target.value }))}
          />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => setForm((s) => ({ ...s, isActive: e.target.checked }))}
          />
          Ativo (entra no rateio de lucros)
        </label>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={save.isPending}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {save.isPending ? 'Salvando…' : isEdit ? 'Salvar alterações' : 'Criar sócio'}
          </button>
        </div>
      </form>
      <Style />
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="space-y-1 block">
      <span className="text-xs font-medium">{label}</span>
      {children}
    </label>
  );
}

function Dialog({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-medium">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Style() {
  return (
    <style>{`
      .input {
        width: 100%;
        border: 1px solid hsl(var(--input));
        background: hsl(var(--background));
        padding: 0.5rem 0.75rem;
        border-radius: 0.375rem;
        font-size: 0.875rem;
      }
      .input:focus { outline: none; box-shadow: 0 0 0 2px hsl(var(--ring) / 0.4); }
    `}</style>
  );
}
